import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import LoginAuthenticationForm from 'components/shared/loginAuthenticationForm';
import EnterOrganizationUrlForm from '../../shared/enterOrganizationLoginForm';
import BackgroundContainer from '../../shared/backgroundContainer';
import http, {
  AUTH,
  TOKEN_TYPE,
  API_DOMAIN,
  API_PROTOCOL,
} from '../../../utility/http';
import StorageService from '../../../utility/storageService';
import { setAuthInfo as setAuth } from '../../../store/modules/auth';
import {
  getOrganizationSettings as getGlobalSettings,
  getAllPresets as getGlobalPresets,
  getOrganizationMenuSetup as getGlobalMenuSetup,
} from '../../../store/modules/organizationSettings';
import { getAuthPermissions as getGlobalAuthPermissions } from '../../../store/modules/accessControl';
import {
  handleCustomHttpError,
  validateOrganizationName,
} from '../../../utility/helpers';
import { useTranslations } from '../../../utility/useTranslations';
import { getSubdomain } from '../../../utility/uiUtils';
import { APP_PAGES } from '../../../constants/pages';
import { API_ME } from '../../../constants/apiRoutes';

const LoginAuthenticationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const translations = useTranslations(APP_PAGES.LOGIN_AUTHENTICATION_PAGE);
  const subDomain = getSubdomain(window.location.hostname);
  const redirectUrl = location?.state?.from?.pathname || '/';

  const [hasRecoveryCodes, setHasRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useParams();
  const dispatch = useDispatch();

  const getAuthPermissions = useCallback(
    () => getGlobalAuthPermissions(dispatch),
    [dispatch]
  );

  const getOrganizationSettings = useCallback(
    () => getGlobalSettings(dispatch),
    [dispatch]
  );

  const getAllPresets = useCallback(
    () => getGlobalPresets(dispatch),
    [dispatch]
  );

  const getOrganizationMenuSetup = useCallback(
    () => getGlobalMenuSetup(dispatch),
    [dispatch]
  );

  const setAuthInfo = useCallback(data => dispatch(setAuth(data)), [dispatch]);

  const validateOrgName = useCallback(() => {
    setIsLoading(true);

    return validateOrganizationName(subDomain)
      .then(({ data }) => {
        if (data.exists) {
          if (token && token.length > 0) {
            http.defaults.headers.Authorization = `${TOKEN_TYPE} ${token}`;
            // fetch user by token
            return Promise.all([
              http.get(API_ME),
              getAuthPermissions(),
              getOrganizationSettings(),
              getOrganizationMenuSetup(),
              getAllPresets(),
            ]).then(([user]) => {
              const authInfo = {
                ...user.data,
                last_login: null,
                access: token,
              };
              setAuthInfo(authInfo);
              StorageService.set(AUTH, JSON.stringify(authInfo));
              return navigate(redirectUrl);
            });
          }
          setIsLoading(false);
        } else {
          window.location = `${API_PROTOCOL}://${API_DOMAIN}${
            window.location.port.length > 0 ? `:${window.location.port}` : ''
          }`;
          setIsLoading(false);
        }
      })
      .catch(error => {
        handleCustomHttpError(error, translations.somethingWentWrong);
      });
  }, [
    navigate,
    subDomain,
    token,
    translations,
    setAuthInfo,
    getAuthPermissions,
    getOrganizationSettings,
    getOrganizationMenuSetup,
    getAllPresets,
    redirectUrl,
  ]);

  useEffect(() => {
    if (subDomain && subDomain.length > 0) {
      validateOrgName();
    }
  }, [subDomain, validateOrgName]);

  if (isLoading) return null;

  if (subDomain && subDomain.length > 0) {
    return (
      <BackgroundContainer
        translations={translations.authContainer}
        removeTopButton
        subDomain={subDomain}
      >
        <LoginAuthenticationForm
          translations={translations}
          navigate={navigate}
          redirectUrl={redirectUrl}
          getAuthPermissions={getAuthPermissions}
          getOrganizationSettings={getOrganizationSettings}
          getAllPresets={getAllPresets}
          getOrganizationMenuSetup={getOrganizationMenuSetup}
          setAuthInfo={setAuthInfo}
          hasRecoveryCodes={hasRecoveryCodes}
          setHasRecoveryCodes={value => setHasRecoveryCodes(value)}
          recoveryCodes={recoveryCodes}
          setRecoveryCodes={value => setRecoveryCodes(value)}
        />
      </BackgroundContainer>
    );
  }

  return (
    <BackgroundContainer
      translations={{
        ...translations,
        ...translations.authContainer,
      }}
    >
      <EnterOrganizationUrlForm translations={translations} />
    </BackgroundContainer>
  );
};

export default memo(LoginAuthenticationPage);
